// The exercise table of a block: one row per exercise, in one grey box. The Block Library cards,
// the hover details, and the drag preview show it read-only. The Schedule cards show it with
// click-to-edit values.
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Button, Typography } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import type { RtpBlockContent, RtpGymExercise } from "./types";

// Same four cells, same order as the exercise form in the Add Block dialog. "Intensity" shortens
// to "Int" (full name in the hover title). The table uses tableLabel. The compact table, in the
// 32px columns of the Schedule cards (40px while all the columns show), uses the shortest compactLabel.
export const EXERCISE_FIELDS = [
	{ field: "sets", tableLabel: "Set", compactLabel: "Set", fullLabel: "Sets" },
	{ field: "reps", tableLabel: "Rep", compactLabel: "Rep", fullLabel: "Reps" },
	{ field: "tempo", tableLabel: "Tempo", compactLabel: "Tem", fullLabel: "Tempo" },
	{ field: "intensity", tableLabel: "Int", compactLabel: "Int", fullLabel: "Intensity" },
] as const;

const EMPTY_VALUE = <span className="rtp-ex-table-empty">—</span>;

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

// A short value that becomes an input on click (antd Typography editable); Enter or blur saves,
// Esc cancels. Read-only when `onSave` is missing (no edit rights, or the drag overlay copy).
// The wrapper stops events from reaching the card: a click would open the modal, Enter/Space
// would too, and a pointer drag inside the input would start a card drag.
// `onEditStart` and `onEditEnd` tell the parent when an edit opens and when it ends (save or cancel).
export const EditableValue = ({
	value,
	onSave,
	required,
	label,
	onEditStart,
	onEditEnd,
}: {
	value?: string;
	onSave?: (next: string) => void;
	required?: boolean;
	label: string;
	onEditStart?: () => void;
	onEditEnd?: () => void;
}) => {
	const [editing, setEditing] = useState(false);
	if (!onSave) return <>{value || EMPTY_VALUE}</>;
	return (
		<span
			className="rtp-editable"
			onClick={stop}
			onKeyDown={stop}
			onPointerDown={editing ? stop : undefined}
		>
			<Typography.Text
				editable={{
					editing,
					text: value ?? "",
					triggerType: ["text"],
					tooltip: false,
					enterIcon: null,
					onStart: () => {
						setEditing(true);
						onEditStart?.();
					},
					onCancel: () => {
						setEditing(false);
						onEditEnd?.();
					},
					onChange: (next) => {
						setEditing(false);
						onEditEnd?.();
						if (next.trim() === (value ?? "").trim()) return;
						if (required && !next.trim()) {
							message.error(`Enter ${label.toLowerCase()}.`);
							return;
						}
						onSave(next);
					},
				}}
			>
				{value || EMPTY_VALUE}
			</Typography.Text>
		</span>
	);
};

// A longer list shows this many rows at first when `collapsible` is set. The user can show the rest.
const EXERCISE_TABLE_COLLAPSED_ROWS = 6;
const EXERCISE_TABLE_NAME_COLUMN = "minmax(0, 1fr)";
// The share of the row for the compact name column. The index is the number of shown value columns.
// A narrow row can give the name less, because each value column keeps its minimum width.
const EXERCISE_TABLE_COMPACT_NAME_SHARE = ["100%", "75%", "65%", "65%", "65%"];
const EXERCISE_TABLE_VALUE_COLUMN = "44px";
const EXERCISE_TABLE_COMPACT_VALUE_COLUMN = "32px";
// The compact columns grow to this width while `revealed` is true. The name column gets narrower.
const EXERCISE_TABLE_REVEALED_VALUE_COLUMN = "40px";

interface ExerciseTableProps {
	exercises: RtpBlockContent["exercises"];
	// Returns the save handler of one cell, or undefined when the cell is read-only.
	onSaveExercise?: (index: number, field: keyof RtpGymExercise) => ((next: string) => void) | undefined;
	// Long lists collapse behind a "+N more" link.
	collapsible?: boolean;
	// Narrow value columns and short header labels, for the Schedule cards.
	compact?: boolean;
}

export const ExerciseTable = ({
	exercises,
	onSaveExercise,
	collapsible = false,
	compact = false,
}: ExerciseTableProps) => {
	const [expanded, setExpanded] = useState(false);
	// True while the table shows all the value columns. An edit in any cell turns it on. It turns off
	// when the user presses outside the table, so the columns do not jump while the user moves between
	// cells. While it is true, the exercise name and the notes show on one line, cut with an ellipsis.
	const [revealed, setRevealed] = useState(false);
	const tableRef = useRef<HTMLDivElement>(null);
	const isEditing = useRef(false);
	// True when the user pressed outside the table during an edit. The edit end then hides the columns.
	const hideAfterEdit = useRef(false);
	// A column shows only when at least one exercise of the entry has a value for it.
	// The check covers all the exercises, so the columns do not change when the list expands.
	// While `revealed` is true, all the columns show, so the user can fill an empty one.
	const shownFields = revealed
		? EXERCISE_FIELDS
		: EXERCISE_FIELDS.filter(({ field }) => exercises.some((ex) => ex[field]));
	// The name column plus one column per shown field. This stays valid with 0 shown fields.
	const compactColumn = revealed ? EXERCISE_TABLE_REVEALED_VALUE_COLUMN : EXERCISE_TABLE_COMPACT_VALUE_COLUMN;
	const nameColumn = compact
		? `minmax(0, ${EXERCISE_TABLE_COMPACT_NAME_SHARE[shownFields.length]})`
		: EXERCISE_TABLE_NAME_COLUMN;
	const valueColumn = compact ? `minmax(${compactColumn}, 1fr)` : EXERCISE_TABLE_VALUE_COLUMN;
	const columns = [nameColumn, ...shownFields.map(() => valueColumn)].join(" ");
	const collapsed = collapsible && !expanded;
	const shownExercises = collapsed ? exercises.slice(0, EXERCISE_TABLE_COLLAPSED_ROWS) : exercises;
	const hiddenCount = collapsible ? Math.max(exercises.length - EXERCISE_TABLE_COLLAPSED_ROWS, 0) : 0;
	const startEdit = () => {
		isEditing.current = true;
		setRevealed(true);
	};
	// The cell must not unmount while the user types in it, so the hide waits for the edit to end.
	const endEdit = () => {
		isEditing.current = false;
		if (!hideAfterEdit.current) return;
		hideAfterEdit.current = false;
		setRevealed(false);
	};
	// While the columns show, a press outside the table hides them. The listener runs in the capture
	// phase, so the card and the edit wrapper cannot stop the event. If an edit is open, the same press
	// blurs the input and saves it. The hide then waits for `endEdit`.
	useEffect(() => {
		if (!revealed) return;
		const hideOnOutsidePress = (event: PointerEvent) => {
			if (tableRef.current?.contains(event.target as Node)) return;
			if (isEditing.current) hideAfterEdit.current = true;
			else setRevealed(false);
		};
		document.addEventListener("pointerdown", hideOnOutsidePress, true);
		return () => document.removeEventListener("pointerdown", hideOnOutsidePress, true);
	}, [revealed]);
	return (
		<>
			<div
				ref={tableRef}
				className={`rtp-ex-card rtp-ex-table${compact ? " rtp-ex-table--compact" : ""}${expanded ? " rtp-ex-table--expanded" : ""}${revealed ? " rtp-ex-table--revealed" : ""}`}
				style={{ "--rtp-ex-columns": columns } as CSSProperties}
			>
				<div className="rtp-ex-table-row rtp-ex-table-row--head">
					<div className="rtp-ex-cell-label rtp-ex-table-name-label">Exercise</div>
					{shownFields.map(({ field, tableLabel, compactLabel, fullLabel }) => (
						<div key={field} className="rtp-ex-cell-label" title={fullLabel}>
							{compact ? compactLabel : tableLabel}
						</div>
					))}
				</div>
				{shownExercises.map((ex, i) => (
					<div key={i} className="rtp-ex-table-row">
						<div className="rtp-ex-table-name" title={revealed ? ex.name : undefined}>
							<span className="rtp-block-ex-name">
								<EditableValue
									value={ex.name}
									onSave={onSaveExercise?.(i, "name")}
									required
									label="An exercise name"
									onEditStart={startEdit}
									onEditEnd={endEdit}
								/>
							</span>
							{ex.notes ? (
								<div className="rtp-ex-table-notes">
									<EditableValue
										value={ex.notes}
										onSave={onSaveExercise?.(i, "notes")}
										label="Notes"
										onEditStart={startEdit}
										onEditEnd={endEdit}
									/>
								</div>
							) : null}
						</div>
						{shownFields.map(({ field, fullLabel }) => (
							<div key={field} className="rtp-block-ex-value">
								<EditableValue
									value={ex[field]}
									onSave={onSaveExercise?.(i, field)}
									label={fullLabel}
									onEditStart={startEdit}
									onEditEnd={endEdit}
								/>
							</div>
						))}
					</div>
				))}
			</div>
			{hiddenCount > 0 ? (
				<Button
					type="link"
					size="small"
					className="rtp-ex-table-toggle"
					data-attr="settings.rtp-block-library.toggle-exercises"
					onClick={() => setExpanded((prev) => !prev)}
				>
					{expanded ? "Show less" : `+${hiddenCount} more`}
				</Button>
			) : null}
		</>
	);
};
